/**
 * Definition Policy Service
 *
 * Reads and writes per-workspace field behavior overrides from/to the
 * workspace_definition_policy table. Missing rows always fall back to
 * code-defined defaults (see STRUCTURAL_FIELD_METAS and Attribute.required).
 */

import { getSupabaseClient } from './supabaseClient';
import type { DefinitionBehavior, DefinitionPolicy } from '../types/prioritization/definitionPolicy';

const TABLE = 'workspace_definition_policy';

interface PolicyRow {
  id: string;
  workspace_id: string;
  field_key: string;
  behavior: DefinitionBehavior;
  updated_at: string;
}

/**
 * Fetch all definition policy overrides for a workspace.
 * Returns a flat Record<fieldKey, behavior> — only keys with explicit overrides are present.
 */
export async function fetchDefinitionPolicy(
  workspaceId: string
): Promise<{ data: DefinitionPolicy; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select('field_key, behavior')
    .eq('workspace_id', workspaceId);

  if (error) return { data: {}, error: error.message };

  const policy: DefinitionPolicy = {};
  for (const row of (data ?? []) as Pick<PolicyRow, 'field_key' | 'behavior'>[]) {
    policy[row.field_key] = row.behavior;
  }
  return { data: policy, error: null };
}

/**
 * Upsert a single field behavior override.
 * title is always 'block' and cannot be changed — callers should guard before calling.
 */
export async function setFieldBehavior(
  workspaceId: string,
  fieldKey: string,
  behavior: DefinitionBehavior,
  userId?: string
): Promise<{ error: string | null }> {
  if (fieldKey === 'title') return { error: null };

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLE)
    .upsert(
      {
        workspace_id: workspaceId,
        field_key: fieldKey,
        behavior,
        updated_at: new Date().toISOString(),
        updated_by: userId ?? null,
      },
      { onConflict: 'workspace_id,field_key' }
    );

  return { error: error?.message ?? null };
}

/**
 * Remove a field override, reverting it to the code default.
 */
export async function resetFieldBehavior(
  workspaceId: string,
  fieldKey: string
): Promise<{ error: string | null }> {
  if (fieldKey === 'title') return { error: null };

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('field_key', fieldKey);

  return { error: error?.message ?? null };
}
